/**
 * The offline draft store: three IndexedDB object stores, exactly as § 4.1 names
 * them.
 *
 * **The principle this whole layer is built to obey (spec, and CLAUDE.md rule 7):
 * BC staging is the source of truth and this is a resilience layer, never a
 * competing store.** Everything below follows from refusing to let the iPad become
 * a second system of record — which is why there is no merge logic here, no
 * derived totals, and deliberately no money.
 *
 * Three properties are load-bearing:
 *
 * **Quantities are strings, in and out.** They arrive as strings and they leave as
 * strings. `7345.00496` cannot survive a round trip through a JS number, and
 * `0.1 + 0.2` is the other half of the argument. The app does no arithmetic on a
 * quantity, so there is nowhere for a float to get in.
 *
 * **`counted_qty: null` is a real, storable value** meaning *not counted*, and it
 * is distinguishable from **no draft record at all** (never touched). Rule 5 is
 * representable in this store, not only in the API — an offline clear has to be a
 * thing the device can hold and then flush, or clearing would not work in a
 * walk-in.
 *
 * **No money, ever.** The item snapshot carries description, UOM, section and the
 * lot marker — what entry needs. Unit cost and on-hand are session-time facts read
 * fresh at review, and caching either would let this app show a manager a dollar
 * figure BC no longer agrees with.
 */
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export const DB_NAME = "lasegunda-inventory";
export const DB_VERSION = 1;

/** Draft sync state, per item. Mirrors what the entry screen shows per row. */
export type DraftSyncState = "pending" | "synced" | "refused";

export interface SessionRecord {
  session_id: string;
  area_id: string;
  status: string;
  owner_upn: string;
  posting_date: string | null;
  /** When the count-list snapshot and staging were last pulled. */
  pulled_at: string;
  /**
   * When every draft for this session was last known to be in staging.
   *
   * **This gates the review screen** (§ 4.2). A review computed from staging that
   * does not yet hold the manager's last five entries is a screen that lies about
   * money, so S5 refuses to open with pending edits and offers "sync now".
   */
  last_full_sync_at: string | null;
  /**
   * Set immediately **before** `POST …/post` is sent, and cleared only by a
   * terminal outcome.
   *
   * The marker is the whole of § 4.5: its presence with no terminal outcome means
   * *we do not know whether this posted*, and the only safe response is to ask BC.
   * Never a reason to re-post.
   */
  post_attempted_at: string | null;
}

/** The count-list snapshot taken at session open, while online. */
export interface ItemRecord {
  key: string;
  session_id: string;
  item_no: string;
  description: string | null;
  uom: string | null;
  /**
   * The units this item may be counted in (item 3). Empty or absent means the
   * only unit is `uom` and no toggle is offered — the case for ~85% of items.
   */
  unit_options?: { code: string; qty_per_base: string | null; is_base: boolean }[];
  section: string | null;
  /** BC's vendor name, verbatim (item 8) — a grouping key like `section`, and
      like `section` not money, so § 4.1's rule is untouched. */
  vendor: string | null;
  lot_tracked: boolean | null;
  blocked: boolean | null;
  /** Which pull this row came from, so a re-pull can be told apart. */
  list_version: string;
}

export interface DraftRecord {
  key: string;
  session_id: string;
  item_no: string;
  /** The typed string, or **null meaning "not counted"** — never a number. */
  counted_qty: string | null;
  typed_at: string;
  device_id: string;
  sync_state: DraftSyncState;
  synced_at: string | null;
  /** The server's own words when a flush was refused. */
  refusal: string | null;
  /**
   * The unit this quantity was counted in (item 3), or `undefined` on every
   * draft written before item 3 existed — which is why it is optional rather
   * than nullable-and-required: an existing IndexedDB row must keep opening.
   * `undefined` and `null` both mean "base unit", the default nothing changed.
   *
   * Held here rather than derived at flush time because a manager can pick BAG
   * in a walk-in and type the number a minute later, and the choice has to
   * survive that gap the same way the quantity does.
   */
  unit_of_measure_code?: string | null;
}

export interface InventoryDB extends DBSchema {
  sessions: { key: string; value: SessionRecord };
  items: { key: string; value: ItemRecord; indexes: { by_session: string } };
  drafts: { key: string; value: DraftRecord; indexes: { by_session: string } };
}

export const itemKey = (sessionId: string, itemNo: string) =>
  `${sessionId}|${itemNo.trim().toUpperCase()}`;

let dbPromise: Promise<IDBPDatabase<InventoryDB>> | null = null;

export function db(): Promise<IDBPDatabase<InventoryDB>> {
  if (!dbPromise) {
    dbPromise = openDB<InventoryDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        database.createObjectStore("sessions", { keyPath: "session_id" });
        const items = database.createObjectStore("items", { keyPath: "key" });
        items.createIndex("by_session", "session_id");
        const drafts = database.createObjectStore("drafts", { keyPath: "key" });
        drafts.createIndex("by_session", "session_id");
      },
    });
  }
  return dbPromise;
}

/**
 * Drop the cached handle **without closing it**.
 *
 * Tests use it between cases; the app has no reason to. It deliberately does not
 * close: a store write the last test left in flight — `clearPostAttempt` after a
 * post's reply, `snapshotItems` from the entry screen's effect — still holds the
 * old connection and finishes against it, whereas a `put` issued after `close()`
 * throws `InvalidStateError` from `transaction()` as an unhandled rejection. The
 * harness swaps in a fresh IndexedDB factory alongside this call, so the next
 * `db()` opens an empty database and the old one is simply never read again.
 */
export function forgetDbHandle(): void {
  dbPromise = null;
}

const DEVICE_KEY = "lasegunda.deviceId";

/**
 * A stable-per-device id, stamped on every draft.
 *
 * Its job is the reconciliation report: "resume on a different iPad" is a promised
 * feature, so when this device overwrites what another one staged, the report can
 * say which device typed what. `localStorage` and not `sessionStorage` — the point
 * is to outlive the tab, since the same iPad should read as the same iPad
 * tomorrow.
 */
export function deviceId(): string {
  try {
    const existing = window.localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const fresh = `ipad-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(DEVICE_KEY, fresh);
    return fresh;
  } catch {
    // Private mode, or storage disabled. A per-load id is still better than
    // failing: the draft store is a resilience layer and must not be the thing
    // that takes entry down.
    return "ipad-unknown";
  }
}
